import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Building2, FileText, Settings, X, Eye, Menu, Cpu, Brain,
  Activity, Zap, FlaskConical, BookOpen, BarChart3, Microscope,
  Search, ChevronLeft, Shield, Sparkles, Filter, Sliders,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAdminContext } from '@/hooks/useAdminContext';
import { cn } from '@/lib/utils';

// ── Nav definitions ────────────────────────────────────────────────

const coreItems = [
  { to: '/admin',            label: 'Organizations', icon: Building2, end: true },
  { to: '/admin/submissions',label: 'Submissions',   icon: FileText },
  { to: '/admin/ai-usage',   label: 'AI Usage',      icon: Cpu },
  { to: '/admin/llm-config', label: 'LLM Config',    icon: Sliders },
  { to: '/admin/system',     label: 'System',        icon: Settings },
];

const growthItems = [
  { to: '/admin/growth/proactive',   label: 'Proactive Pipeline', icon: Sparkles },
  { to: '/admin/growth/nextaction',  label: 'Next Best Actions', icon: Zap },
  { to: '/admin/growth/pulse',       label: 'Platform Pulse',    icon: Activity },
  { to: '/admin/growth/thinking',    label: 'Thinking Layer',    icon: Brain },
  { to: '/admin/growth/experiments', label: 'Experiments',       icon: FlaskConical },
  { to: '/admin/growth/playbook',    label: 'Playbook',          icon: BookOpen },
  { to: '/admin/growth/reports',     label: 'Success Reports',   icon: BarChart3 },
  { to: '/admin/growth/digest',      label: 'AiA Digest',        icon: FileText },
  { to: '/admin/growth/research',    label: 'Research Feed',     icon: Microscope },
  { to: '/admin/growth/gaps',        label: 'Feature Gaps',      icon: Search },
];

// ── Shared nav link renderer ───────────────────────────────────────

function SidebarNavLink({
  to,
  label,
  icon: Icon,
  end,
  onClick,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
  onClick?: () => void;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full',
          isActive
            ? 'bg-sidebar-accent text-foreground'
            : 'text-muted-foreground hover:text-foreground hover:bg-sidebar-accent'
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

// ── Sidebar content (used in both desktop and mobile Sheet) ────────

function SidebarContent({ onNavClick }: { onNavClick?: () => void }) {
  const navigate = useNavigate();
  const { orgs, orgsLoading, selectedOrgId, setSelectedOrg } = useAdminContext();
  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-sidebar-border flex items-center gap-2">
        <div className="h-7 w-7 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
          <Shield className="h-4 w-4 text-primary" />
        </div>
        <span className="font-semibold text-sm text-foreground">Admin</span>
      </div>

      {/* Org filter */}
      <div className="px-3 pt-3 pb-1">
        <div className="flex items-center gap-1.5 px-1 pb-1.5">
          <Filter className="h-3 w-3 text-muted-foreground/60" />
          <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/40">Organization</span>
        </div>
        <Select
          value={selectedOrgId || '__all__'}
          onValueChange={(v) => setSelectedOrg(v === '__all__' ? null : v)}
          disabled={orgsLoading}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder={orgsLoading ? 'Loading...' : 'All organizations'} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All organizations</SelectItem>
            {orgs.map(org => (
              <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        {/* Core section */}
        {coreItems.map(item => (
          <SidebarNavLink
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            end={item.end}
            onClick={onNavClick}
          />
        ))}

        {/* Growth Intelligence section */}
        <div className="h-px bg-sidebar-border my-3" />
        <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/40">
          Growth Intelligence
        </p>
        {growthItems.map(item => (
          <SidebarNavLink
            key={item.to}
            to={item.to}
            label={item.label}
            icon={item.icon}
            onClick={onNavClick}
          />
        ))}
      </nav>

      {/* Back to app */}
      <div className="p-3 border-t border-sidebar-border">
        <button
          onClick={() => { onNavClick?.(); navigate('/profile'); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-sidebar-accent transition-colors"
        >
          <ChevronLeft className="h-4 w-4 shrink-0" />
          <span>Back to app</span>
        </button>
      </div>
    </div>
  );
}

// ── Main layout ────────────────────────────────────────────────────

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isImpersonating, impersonatedOrgName, exitImpersonation } = useAdminContext();
  const navigate = useNavigate();

  const handleExitImpersonation = () => {
    exitImpersonation();
    navigate('/admin');
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 h-screen sticky top-0 bg-sidebar border-r border-sidebar-border flex-col shrink-0">
        <SidebarContent />
      </aside>

      {/* Main content column */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center gap-3 px-4 py-3 border-b bg-sidebar border-sidebar-border">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="-ml-1">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-64 bg-sidebar border-sidebar-border">
              <SidebarContent onNavClick={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="font-semibold text-sm">Admin</span>
          </div>
        </header>

        {/* Impersonation Banner */}
        {isImpersonating && (
          <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-center gap-4">
            <Eye className="h-4 w-4 shrink-0" />
            <span className="font-medium text-sm">
              Viewing as Client Admin for <strong>{impersonatedOrgName}</strong>
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExitImpersonation}
              className="bg-amber-100 border-amber-600 text-amber-900 hover:bg-amber-200"
            >
              <X className="h-4 w-4 mr-1" />
              Exit View
            </Button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-auto px-4 md:px-6 py-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
